export const PAGE_GAP = "gap";

/**
 * Returns the page numbers a numbered pager shows, with `PAGE_GAP` standing in for a run it skips.
 * The first and last page are always present, along with `window` pages either side of the current
 * one, so the list keeps a stable width as the reader moves through it.
 *
 * `buildPageList(6, 20)` → `[1, "gap", 4, 5, 6, 7, 8, "gap", 20]`
 *
 * @param {Number} page - current page, 1-based
 * @param {Number} pageCount
 * @param {Object} [options]
 * @param {Number} [options.window] - pages shown either side of the current one
 * @returns {Array<Number|String>}
 */
export function buildPageList(page, pageCount, { window = 2 } = {}) {
  const total = Math.max(1, Math.floor(pageCount) || 1);
  const current = Math.min(Math.max(Math.floor(page) || 1, 1), total);

  const shown = new Set([1, total]);
  for (let offset = -window; offset <= window; offset += 1) {
    const candidate = current + offset;

    if (candidate >= 1 && candidate <= total) {
      shown.add(candidate);
    }
  }

  const ordered = [...shown].sort((left, right) => left - right);
  const list = [];

  ordered.forEach((number, index) => {
    const previous = ordered[index - 1];

    if (previous !== undefined) {
      // A gap standing for one page takes more room than the page, so show the page.
      if (number - previous === 2) {
        list.push(previous + 1);
      } else if (number - previous > 1) {
        list.push(PAGE_GAP);
      }
    }

    list.push(number);
  });

  return list;
}
